/**
 * SubscriptionPage - planos, selector de período, pagamento manual (upload comprovativo).
 */
import { useEffect, useState, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    RadioGroup, RadioGroupItem,
} from "@/components/ui/radio-group";
import { Sparkles, Check, Upload, FileImage, Loader2, Crown, Building2, Smartphone, QrCode, Receipt } from "lucide-react";
import { api, getApiErrorMessage } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import StatusBadge from "@/components/shared/StatusBadge";
import BillingPeriodToggle from "@/components/shared/BillingPeriodToggle";
import { fmtDateTime } from "@/lib/date";
import {
    DEFAULT_BILLING_PERIOD,
    getPlanPricing,
    getPeriod,
    formatTotal,
    formatMonthly,
    formatSavings,
} from "@/lib/billing";

export default function SubscriptionPage() {
    const { refreshUser, user } = useAuth();
    const { currency, setCurrency } = useCurrency();
    const [plans, setPlans] = useState([]);
    const [period, setPeriod] = useState(DEFAULT_BILLING_PERIOD);
    const [sub, setSub] = useState(null);
    const [bank, setBank] = useState(null);
    const [open, setOpen] = useState(false);
    const [pickedPlan, setPickedPlan] = useState(null);
    const [method, setMethod] = useState("transferencia");
    const [reference, setReference] = useState("");
    const [proof, setProof] = useState(null); // dataURL
    const [proofName, setProofName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const fileRef = useRef(null);

    const load = async () => {
        const [p, s, b] = await Promise.all([
            api.get("/public/plans"),
            api.get("/subscriptions/me"),
            api.get("/payments/bank-info"),
        ]);
        setPlans(p.data);
        setSub(s.data);
        setBank(b.data);
    };
    useEffect(() => { load(); }, []);

    const onUpload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 5 * 1024 * 1024) {
            toast.error("Ficheiro muito grande (máx 5 MB).");
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
            setProof(ev.target.result);
            setProofName(f.name);
        };
        reader.readAsDataURL(f);
    };

    const submit = async () => {
        if (!proof) { toast.error("Carregue o comprovativo de pagamento."); return; }
        setSubmitting(true);
        try {
            await api.post("/payments/submit", {
                plan_id: pickedPlan.id,
                billing_period: period,
                currency,
                method,
                proof_data_url: proof,
                proof_filename: proofName,
                reference,
            });
            toast.success("Comprovativo enviado!", {
                description: "A equipa MeSafa irá validar nas próximas horas.",
            });
            setOpen(false);
            setProof(null);
            setProofName("");
            setReference("");
            await load();
            await refreshUser();
        } catch (err) {
            toast.error(getApiErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    const currentPlan = sub?.plan;
    const periodInfo = getPeriod(period);
    const pickedPricing = pickedPlan ? getPlanPricing(pickedPlan, period) : null;

    return (
        <div className="space-y-6" data-testid="subscription-page">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                        Planos e Subscrição
                    </h1>
                    <p className="text-muted-foreground">Gerencie a sua subscrição do MeSafa.ao.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex items-center gap-2 rounded-full bg-secondary/40 border border-border px-1 py-1">
                        {["AOA", "USD"].map((c) => (
                            <button
                                key={c}
                                onClick={() => setCurrency(c)}
                                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                                    currency === c ? "bg-foreground text-background font-semibold" : "text-muted-foreground hover:text-foreground"
                                }`}
                                data-testid={`subscription-currency-${c.toLowerCase()}`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                    <BillingPeriodToggle
                        value={period}
                        onChange={setPeriod}
                        variant="dark"
                        testId="subscription-period-toggle"
                    />
                </div>
            </div>

            {periodInfo.discount_pct > 0 && (
                <Card
                    className="glass-card p-3 rounded-2xl border-[#2DD4BF]/30 bg-[#2DD4BF]/5"
                    data-testid="subscription-period-discount-banner"
                >
                    <p className="text-sm text-foreground">
                        ✨ A pagar <strong>{periodInfo.short_label.toLowerCase()}</strong> tem{" "}
                        <strong>{periodInfo.discount_pct}% de desconto</strong> em relação ao plano mensal.
                    </p>
                </Card>
            )}

            {/* Current plan */}
            <Card className="glass-card p-5 rounded-2xl flex flex-col sm:flex-row sm:items-center gap-4" data-testid="current-subscription">
                <div className="h-14 w-14 rounded-2xl btn-grad-purple grid place-items-center text-white">
                    <Crown className="h-6 w-6" />
                </div>
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <h3 className="font-display font-semibold text-lg">
                            {currentPlan?.name || "Sem plano ativo"}
                        </h3>
                        <StatusBadge status={sub?.status === "ativa" ? "aprovado" : sub?.status === "trial" ? "pendente" : "rejeitado"} />
                    </div>
                    <div className="text-sm text-muted-foreground">
                        {sub?.status === "ativa"
                            ? `Válida até ${fmtDateTime(sub.expires_at)}`
                            : "Em modo trial — faça upgrade para desbloquear todas as funcionalidades."}
                    </div>
                </div>
                {currentPlan && (
                    <div className="text-right">
                        <div className="font-display text-2xl font-bold">
                            {currency === "USD" ? `${currentPlan.price_usd} USD` : `${Number(currentPlan.price_aoa).toLocaleString("pt-PT")} AOA`}
                        </div>
                        <div className="text-xs text-muted-foreground">/mês</div>
                    </div>
                )}
            </Card>

            {/* Plans grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {plans.map((p) => {
                    const isCustom = p.contact_only || p.id === "agencia";
                    const pricing = isCustom ? null : getPlanPricing(p, period);
                    const monthlyLabel = isCustom ? null : formatMonthly(pricing, currency);
                    const totalLabel = isCustom ? null : formatTotal(pricing, currency);
                    const savings = isCustom ? null : formatSavings(pricing, currency);
                    return (
                        <Card
                            key={p.id}
                            className={`p-5 rounded-2xl bg-card relative ${
                                p.highlight ? "border-[#6B21D9] ring-1 ring-[#6B21D9]/30" : "border-border"
                            } ${currentPlan?.id === p.id ? "opacity-90" : ""}`}
                            data-testid={`subscription-plan-${p.id}`}
                        >
                            {p.highlight && (
                                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 btn-grad-purple text-white border-none gap-1">
                                    <Sparkles className="h-3 w-3" /> Mais escolhido
                                </Badge>
                            )}
                            <h3 className="font-display font-semibold text-lg">{p.name}</h3>
                            <p className="text-sm text-muted-foreground">{p.tagline}</p>
                            <div className="my-4 min-h-[80px]">
                                {isCustom ? (
                                    <span className="font-display text-xl font-bold">Personalizado</span>
                                ) : (
                                    <>
                                        <div>
                                            <span className="font-display text-3xl font-bold">{monthlyLabel}</span>
                                            <span className="text-muted-foreground text-sm"> /mês</span>
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">
                                            {periodInfo.months > 1 ? (
                                                <>
                                                    Total {periodInfo.short_label.toLowerCase()}:{" "}
                                                    <span className="font-medium text-foreground">{totalLabel}</span>
                                                    {savings && (
                                                        <>
                                                            {" · "}
                                                            <span className="text-[#2DD4BF] font-medium">poupa {savings}</span>
                                                        </>
                                                    )}
                                                </>
                                            ) : (
                                                "Faturação mensal"
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                            <ul className="space-y-2 mb-4 text-sm">
                                {p.features.slice(0, 5).map((f, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <Check className="h-4 w-4 text-[#2DD4BF] shrink-0 mt-0.5" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            {currentPlan?.id === p.id ? (
                                <Button variant="outline" className="w-full" disabled>Plano atual</Button>
                            ) : isCustom ? (
                                <Button
                                    asChild
                                    className="w-full"
                                    variant="outline"
                                    data-testid={`subscription-contact-${p.id}-button`}
                                >
                                    <a href="mailto:negocios@mesafa.ao?subject=Pedido%20de%20cota%C3%A7%C3%A3o%20-%20Plano%20Ag%C3%AAncia">
                                        Falar com vendas
                                    </a>
                                </Button>
                            ) : (
                                <Button
                                    className={`w-full ${p.highlight ? "btn-grad-purple text-white" : ""}`}
                                    variant={p.highlight ? "default" : "outline"}
                                    onClick={() => { setPickedPlan(p); setOpen(true); }}
                                    data-testid={`subscription-upgrade-${p.id}-button`}
                                >
                                    Subscrever {periodInfo.short_label}
                                </Button>
                            )}
                        </Card>
                    );
                })}
            </div>

            <Card className="glass-card p-4 rounded-2xl border-[#6B21D9]/20" data-testid="subscription-invoice-note">
                <p className="text-sm text-muted-foreground">
                    <strong className="text-foreground">Faturas-recibo:</strong> Emitidas pela MUDS - Prestação de Serviços e Comércio Geral (SU), Lda mediante solicitação do cliente, após confirmação do pagamento. Para pedir a sua, envie email para{" "}
                    <a href="mailto:geral@mesafa.ao" className="text-[#A78BFA] hover:underline">geral@mesafa.ao</a>{" "}
                    com a referência do pagamento.
                </p>
            </Card>

            {/* Payment history */}
            <Card className="glass-card p-5 rounded-2xl" data-testid="payments-history">
                <h3 className="font-display font-semibold mb-4">Histórico de pagamentos</h3>
                {(sub?.payments || []).length === 0 ? (
                    <div className="text-sm text-muted-foreground text-center py-6">Sem pagamentos até ao momento.</div>
                ) : (
                    <ul className="divide-y divide-white/5">
                        {sub.payments.map((p) => (
                            <li key={p.id} className="py-3 flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <div className="text-sm font-medium">
                                        {p.plan_id} · {
                                            p.method === "multicaixa_express" ? "Multicaixa Express"
                                            : p.method === "e_kwanza" ? "É-Kwanza"
                                            : p.method === "referencia_mc" ? "Referência Multicaixa"
                                            : "Transferência"
                                        }
                                        {p.billing_period && p.billing_period !== "mensal" && (
                                            <Badge className="ml-2 bg-[#6B21D9]/15 text-[#A78BFA] border-[#6B21D9]/30 border text-[10px]">
                                                {p.billing_period}
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="text-xs text-muted-foreground">{fmtDateTime(p.created_at)}</div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-semibold">
                                        {p.currency === "USD" ? `${p.amount_usd} USD` : `${p.amount_aoa.toLocaleString("pt-PT")} AOA`}
                                    </span>
                                    <StatusBadge status={p.status} />
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>

            {/* Upgrade dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            Subscrever {pickedPlan?.name} · {periodInfo.short_label}
                        </DialogTitle>
                        <DialogDescription>
                            Faça o pagamento e envie o comprovativo. Validamos em poucas horas.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-5">
                        <Card className="p-4 rounded-xl bg-card border-border">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                    <div className="text-xs text-muted-foreground">A pagar agora ({periodInfo.short_label.toLowerCase()})</div>
                                    <div
                                        className="font-display text-2xl font-bold"
                                        data-testid="subscription-dialog-total"
                                    >
                                        {pickedPricing && formatTotal(pickedPricing, currency)}
                                    </div>
                                    {pickedPricing?.months > 1 && (
                                        <div className="text-xs text-muted-foreground mt-1">
                                            Equivale a <span className="font-medium text-foreground">{formatMonthly(pickedPricing, currency)}/mês</span>
                                            {formatSavings(pickedPricing, currency) && (
                                                <> · <span className="text-[#2DD4BF] font-medium">poupa {formatSavings(pickedPricing, currency)}</span></>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                    <Badge className="bg-[#6B21D9]/15 text-[#A78BFA] border-[#6B21D9]/30 border">
                                        {pickedPlan?.name}
                                    </Badge>
                                    <Badge className="bg-[#2DD4BF]/15 text-[#2DD4BF] border-[#2DD4BF]/30 border">
                                        {periodInfo.short_label}
                                    </Badge>
                                </div>
                            </div>
                        </Card>

                        <div>
                            <Label className="mb-2 block">Período de subscrição</Label>
                            <RadioGroup
                                value={period}
                                onValueChange={setPeriod}
                                className="grid grid-cols-2 sm:grid-cols-4 gap-2"
                            >
                                {[
                                    { id: "mensal", label: "Mensal", note: "Sem desconto" },
                                    { id: "trimestral", label: "Trimestral", note: "Poupa 10%" },
                                    { id: "semestral", label: "Semestral", note: "Poupa 15%" },
                                    { id: "anual", label: "Anual", note: "Poupa 25%" },
                                ].map((opt) => (
                                    <Label
                                        key={opt.id}
                                        htmlFor={`period-${opt.id}`}
                                        className={`p-3 rounded-xl border cursor-pointer flex flex-col gap-1 ${
                                            period === opt.id ? "border-[#6B21D9] bg-[#6B21D9]/10" : "border-border"
                                        }`}
                                        data-testid={`subscription-dialog-period-${opt.id}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <RadioGroupItem value={opt.id} id={`period-${opt.id}`} />
                                            <span className="font-medium text-sm">{opt.label}</span>
                                        </div>
                                        <span className="text-[11px] text-muted-foreground ml-5">{opt.note}</span>
                                    </Label>
                                ))}
                            </RadioGroup>
                        </div>

                        <div>
                            <Label className="mb-2 block">Método de pagamento</Label>
                            <RadioGroup value={method} onValueChange={setMethod} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {Object.entries(bank?.methods || {})
                                    .filter(([, m]) => m.active)
                                    .map(([key, m]) => {
                                        const Icon = key === "transferencia" ? Building2
                                            : key === "multicaixa_express" ? Smartphone
                                            : key === "e_kwanza" ? QrCode
                                            : Receipt;
                                        return (
                                            <Label
                                                key={key}
                                                htmlFor={`m-${key}`}
                                                className={`p-3 rounded-xl border cursor-pointer flex gap-3 items-start ${
                                                    method === key ? "border-[#6B21D9] bg-[#6B21D9]/10" : "border-border"
                                                }`}
                                                data-testid={`subscription-method-${key}`}
                                            >
                                                <RadioGroupItem value={key} id={`m-${key}`} />
                                                <div>
                                                    <div className="flex items-center gap-2 font-medium text-sm">
                                                        <Icon className="h-4 w-4" /> {m.label}
                                                    </div>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        {m.description}
                                                        {m.phone && <><br />Tel: {m.phone}</>}
                                                    </p>
                                                </div>
                                            </Label>
                                        );
                                    })}
                            </RadioGroup>
                        </div>

                        {method === "transferencia" && bank?.bank && (
                            <Card className="p-3 rounded-xl bg-secondary/40 text-sm space-y-1">
                                <div><span className="text-muted-foreground">Banco:</span> {bank.bank.bank_name}</div>
                                <div><span className="text-muted-foreground">Titular:</span> {bank.bank.holder}</div>
                                {bank.bank.account_number && <div><span className="text-muted-foreground">N.º conta:</span> <span className="font-mono">{bank.bank.account_number}</span></div>}
                                {bank.bank.nib && <div><span className="text-muted-foreground">NIB:</span> <span className="font-mono">{bank.bank.nib}</span></div>}
                                <div><span className="text-muted-foreground">IBAN:</span> <span className="font-mono">{bank.bank.iban}</span></div>
                                {bank.bank.swift && <div><span className="text-muted-foreground">SWIFT:</span> <span className="font-mono">{bank.bank.swift}</span></div>}
                                <div><span className="text-muted-foreground">Referência:</span> {bank.bank.reference_prefix || "MeSafa"}-{user?.id?.slice(0, 6)}-{pickedPlan?.id}-{period}</div>
                            </Card>
                        )}

                        <div>
                            <Label>Referência/número do pagamento</Label>
                            <Input
                                placeholder="Ex: REF12345678"
                                value={reference}
                                onChange={(e) => setReference(e.target.value)}
                                data-testid="subscription-reference-input"
                            />
                        </div>

                        <div>
                            <Label>Comprovativo (imagem ou PDF)</Label>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*,application/pdf"
                                className="hidden"
                                onChange={onUpload}
                                data-testid="subscription-upload-proof-input"
                            />
                            {proof ? (
                                <Card className="p-3 rounded-xl bg-secondary/40 mt-1 flex items-center gap-3">
                                    <FileImage className="h-8 w-8 text-[#A78BFA]" />
                                    <div className="flex-1">
                                        <div className="text-sm font-medium">{proofName}</div>
                                        <div className="text-xs text-muted-foreground">Pronto para envio</div>
                                    </div>
                                    <Button variant="outline" size="sm" onClick={() => { setProof(null); setProofName(""); }}>
                                        Trocar
                                    </Button>
                                </Card>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => fileRef.current?.click()}
                                    className="mt-1 w-full p-6 border-2 border-dashed border-white/10 rounded-xl text-center text-sm text-muted-foreground hover:border-[#6B21D9]/40"
                                    data-testid="subscription-upload-trigger"
                                >
                                    <Upload className="h-5 w-5 mx-auto mb-2" />
                                    Clique para carregar o comprovativo
                                </button>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={submit} disabled={submitting} className="btn-grad-purple text-white" data-testid="subscription-submit-proof-button">
                            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                            Enviar comprovativo
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
